import React from 'react';
import { InView } from 'react-intersection-observer';

import useEventTracking from '@hooks/useEventTracking';
import { ImpressionElement, TrackingLocation, impression } from '@tracking';

import OrgChartEmployeeTopLevel from './EmployeeTopLevel';
import useOrgChart from './OrgChartContext';
import { EMPLOYEE_CARD_WIDTH } from './constants';
import { OrgChartCompanyReportsSectionProps } from './interfaces';
import { useEmployeeMasonryLayout } from './useMasonryLayout';

const OrgChartCompanyReportsSection = (props: OrgChartCompanyReportsSectionProps) => {
  const { expandedPath, expandedEmployees } = useOrgChart();
  const reportsIds = props.employeeWithReports?.reports ?? [];
  const reports = props.employees.filter((employee) => reportsIds?.includes(employee.id)) ?? [];

  const { containerRef, gridColumns, masonryLayout } = useEmployeeMasonryLayout({
    reports,
    expandedEmployees,
    columnWidth: EMPLOYEE_CARD_WIDTH,
    containerPadding: 96,
    dependencies: [props.employeeWithReports], // Re-calculate when this changes
  });

  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.OrgChartCompanyReportsSection,
  });

  return (
    <Track>
      <InView
        className="w-full"
        onChange={(inView) => {
          inView &&
            trackEvent(impression({ impressionElement: ImpressionElement.OrgChartReportsSection }));
        }}
      >
        <div ref={containerRef} className="mt-20 w-full px-12">
          <div className="mx-auto flex w-fit justify-center gap-24">
            {Array.from({ length: gridColumns }).map((_, colIndex) => (
              <div
                key={colIndex}
                className="flex flex-col gap-24"
                style={{ width: `${EMPLOYEE_CARD_WIDTH}px` }}
              >
                {masonryLayout
                  .filter((layoutItem) => layoutItem.column === colIndex)
                  .map(({ item: employee }) => {
                    const isSelected = expandedPath.includes(employee.id);

                    return (
                      <OrgChartEmployeeTopLevel
                        key={employee.id}
                        id={employee.id}
                        name={employee.name}
                        position={employee.title}
                        imageUrl={employee.imageUrl}
                        level={employee.level}
                        isRootLevel={false}
                        isSelected={isSelected}
                        reports={
                          employee.reports?.map((reportId) =>
                            props.employees.find((e) => e.id === reportId)
                          ) ?? []
                        }
                        isExpanded={isSelected}
                      />
                    );
                  })}
              </div>
            ))}
          </div>
        </div>
      </InView>
    </Track>
  );
};

export default OrgChartCompanyReportsSection;
